import React, { useRef } from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line import/order
import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { JsmPageInteractiveEvent } from '@atlassian/jira-servicedesk-common/src/utils/browser-metric/index.tsx';
import type { ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useRequestSecuritySettings } from '../services/request-security';
import type { SecuritySettingsResponse } from '../services/types';
import { CustomerPermissionsForm } from './form';
import { Header } from './header';

export type CustomerPermissionsLayoutProps = {
	isProjectSimplified: boolean;
	source: SecuritySettingsResponse;
	projectKey: ProjectKey;
	onResourceUpdate?: (settings: SecuritySettingsResponse) => void;
};

export const CustomerPermissionsAppUi = ({
	source,
	projectKey,
	onResourceUpdate,
	isProjectSimplified,
}: CustomerPermissionsLayoutProps) => {
	const [{ data, loading, error }, { saveRequestSecurity }] = useRequestSecuritySettings({
		source,
		projectKey,
		onResourceUpdate,
	});

	// Keep track of whether this page has ever not been in a loading state;
	// we don't want to unmount and remount the JsmPageInteractiveEvent component
	// below or we'll get duplicate metrics
	const hasLoaded = useRef<boolean>(false);
	if (!loading && !hasLoaded.current) {
		hasLoaded.current = true;
	}

	return (
		<Container>
			<Header />
			<CustomerPermissionsForm
				loading={loading}
				error={error}
				settings={data}
				projectKey={projectKey}
				isProjectSimplified={isProjectSimplified}
				onSaveRequestSecurity={saveRequestSecurity}
			/>
			{hasLoaded.current && <JsmPageInteractiveEvent />}
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${gridSize * 100}px`,
	marginBottom: token('space.200', '16px'),
});
